import { useState, useCallback, useContext, useEffect, CSSProperties } from 'react';
import classNames from 'classnames';
import { message, Modal, Form, Select, Checkbox, Tooltip } from 'antd';
import { SoundOutlined } from '@ant-design/icons';
import ZoomContext from '../../../context/zoom-context';
import CameraButton, { videoProcessor } from './camera';
import MicrophoneButton from './microphone';
import { ScreenShareButton } from './screen-share';
import { VideoBackgroundButton } from './video-background-button';
import AudioVideoStatisticModal from './audio-video-statistic';
import ZoomMediaContext from '../../../context/media-context';
import { useUnmount, useMount } from '../../../hooks';
import type { MediaDevice } from '../video-types';
import './video-footer.scss';
import { isAndroidOrIOSBrowser } from '../../../utils/platform';
import { getPhoneCallStatusDescription } from '../video-constants';
import { type RecordButtonProps, getRecordingButtons, RecordingButton } from './recording';
import {
  type DialOutOption,
  type Processor,
  DialoutState,
  RecordingStatus,
  MutedSource,
  AudioChangeAction,
  SharePrivilege,
  MobileVideoFacingMode,
  LiveStreamStatus,
  ShareStatus,
  CommandChannelMsg,
  ProcessorParams
} from '@zoom/videosdk';
import { LiveTranscriptionButton } from './live-transcription';
import { LeaveButton } from './leave';
import { TranscriptionSubtitle } from './transcription-subtitle';
import IsoRecordingModal from './recording-ask-modal';
import { LiveStreamButton, LiveStreamModal } from './live-stream';
import { IconFont } from '../../../component/icon-font';
import { VideoMaskModel } from './video-mask-modal';
import { useParticipantsChange } from '../hooks/useParticipantsChange';
import PermissionInstructionModal from '../../preview/permission-instruction-modal';
import PermissionRequestModal from '../../preview/permission-request-modal';
import {
  getPoseDetectionButtons,
  PoseDetectionButton,
  type PoseDetectionProps,
  PoseDetectionStatus
} from './pose-detection';
import { ProcessorName, UserRole, ViewContext } from '../../../App';
import { CommandReceiver, CommandRecord } from '../../command/cmd-types';
import produce from 'immer';
import { GalleryViewButton, GalleryViewStatus, getGalleryViewButtons, type GalleryViewProps } from './gallery-view';
import { useHistory } from 'react-router-dom';
import { FullScreenButton, FullScreenProps, FullScreenStatus, getFullScreenButtons } from './full-screen';
import PoseDetectionLoadingModal from './pose-detection-loading-modal';
import ConfirmOverlay from './confirm-overlay';

interface VideoFooterProps {
  className?: string;
  selfShareCanvas?: HTMLCanvasElement | HTMLVideoElement | null;
  sharing?: boolean;
  slotBookingId?: string;
  userId?: string;
  onMobileChangeRatio?: (userId: number, isLandscape: boolean) => void;
  initialIsStartedAudio?: boolean;
  initialIsStartedVideo?: boolean;
  initialIsMuted?: boolean;
  initialHasMicrophonePermission?: boolean | null;
  initialHasCameraPermission?: boolean | null;
  initialActiveMicrophone?: string;
  initialActiveCamera?: string;
  initialActiveSpeaker?: string;
}

const oneToAllUser = {
  audio: '',
  avatar: '',
  bVideoOn: false,
  displayName: 'To All',
  isHost: false,
  isManager: false,
  muted: false,
  sharerOn: undefined,
  sharerPause: undefined,
  userId: 0
};

const isAudioEnable = typeof AudioWorklet === 'function';

const getBrowserPermissionInstructions = (deviceName: string): string => {
  const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  const isFirefox = /Firefox/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  const isEdge = /Edg/.test(navigator.userAgent);

  if (isChrome || isEdge) {
    return `1. Click the lock icon (i) in the address bar\n2. Find "${deviceName === 'microphone' ? 'Microphone' : deviceName === 'camera' ? 'Camera' : 'Microphone and Camera'}"\n3. Change it to "Allow"\n4. Refresh the page`;
  } else if (isFirefox) {
    return `1. Click the lock icon (🔒) in the address bar\n2. Click "More Information"\n3. Go to "Permissions" tab\n4. Find "${deviceName === 'microphone' ? 'Use the Microphone' : deviceName === 'camera' ? 'Use the Camera' : 'Use the Microphone and Camera'}"\n5. Change to "Allow"\n6. Refresh the page`;
  } else if (isSafari) {
    return `1. Go to Safari > Settings > Websites\n2. Find "${deviceName === 'microphone' ? 'Microphone' : deviceName === 'camera' ? 'Camera' : 'Microphone and Camera'}"\n3. Find this website and change to "Allow"\n4. Refresh the page`;
  } else {
    return `Please check your browser settings and allow ${deviceName} access for this website, then refresh the page.`;
  }
};

const Divider = () => (
  <div
    style={{
      marginLeft: 6,
      marginRight: 6,
      width: '1px',
      height: '36px',
      background: 'rgba(255,255,255,0.12)',
      borderRadius: '1px',
      flexShrink: 0
    }}
  />
);

const VideoFooter = (props: VideoFooterProps) => {
  const {
    className,
    selfShareCanvas,
    sharing,
    slotBookingId,
    userId,
    initialIsStartedAudio,
    initialIsStartedVideo,
    initialIsMuted,
    initialHasMicrophonePermission,
    initialHasCameraPermission,
    initialActiveMicrophone,
    initialActiveCamera,
    initialActiveSpeaker,
    onMobileChangeRatio
  } = props;
  const zmClient = useContext(ZoomContext);
  const cmdClient = zmClient.getCommandClient();
  const [commandReceivers, setCommandReceivers] = useState<CommandReceiver[]>([]);
  const [commandRecords, setCommandRecords] = useState<CommandRecord[]>([]);

  const [currentUserId, setCurrentUserId] = useState<number>(0);
  const { mediaStream } = useContext(ZoomMediaContext);
  const liveTranscriptionClient = zmClient.getLiveTranscriptionClient();
  const liveStreamClient = zmClient.getLiveStreamClient();
  const recordingClient = zmClient.getRecordingClient();
  const [isStartedAudio, setIsStartedAudio] = useState(false);
  const [requestedAudioOn] = useState(initialIsStartedAudio ?? true);
  const [isStartedVideo, setIsStartedVideo] = useState(
    initialIsStartedVideo ?? zmClient.getCurrentUserInfo()?.bVideoOn
  );
  const [audio, setAudio] = useState(zmClient.getCurrentUserInfo()?.audio);
  const [isSupportPhone, setIsSupportPhone] = useState(false);
  const [phoneCountryList, setPhoneCountryList] = useState<any[]>([]);
  const [phoneCallStatus, setPhoneCallStatus] = useState<DialoutState>();
  const [isStartedLiveTranscription, setIsStartedLiveTranscription] = useState(false);
  const [isDisableCaptions, setIsDisableCaptions] = useState(false);
  const [isMirrored, setIsMirrored] = useState(false);
  const [isBlur, setIsBlur] = useState(mediaStream?.getVirtualbackgroundStatus().imageSrc === 'blur');
  const [isMuted, setIsMuted] = useState(initialIsMuted ?? false /*?? !!zmClient.getCurrentUserInfo()?.muted*/);
  const [activeMicrophone, setActiveMicrophone] = useState(
    initialActiveMicrophone ?? mediaStream?.getActiveMicrophone()
  );
  const [activeSpeaker, setActiveSpeaker] = useState(initialActiveSpeaker ?? mediaStream?.getActiveSpeaker());
  const [activeCamera, setActiveCamera] = useState(initialActiveCamera ?? mediaStream?.getActiveCamera());
  const [micList, setMicList] = useState<MediaDevice[]>(mediaStream?.getMicList() ?? []);
  const [speakerList, setSpeakerList] = useState<MediaDevice[]>(mediaStream?.getSpeakerList() ?? []);
  const [cameraList, setCameraList] = useState<MediaDevice[]>(mediaStream?.getCameraList() ?? []);
  const [statisticVisible, setStatisticVisible] = useState(false);
  const [selecetedStatisticTab, setSelectedStatisticTab] = useState('audio');
  const [isComputerAudioDisabled, setIsComputerAudioDisabled] = useState(false);
  const [sharePrivilege, setSharePrivileg] = useState(SharePrivilege.Unlocked);
  const [caption, setCaption] = useState({ text: '', isOver: false });
  const [activePlaybackUrl, setActivePlaybackUrl] = useState('');
  const [activeProcessor, setActiveProcessor] = useState<Processor | undefined>();
  const [isMicrophoneForbidden, setIsMicrophoneForbidden] = useState(false);
  const [hasMicrophonePermission, setHasMicrophonePermission] = useState<boolean | null>(
    initialHasMicrophonePermission ?? null
  );
  const [hasCameraPermission, setHasCameraPermission] = useState<boolean | null>(initialHasCameraPermission ?? null);
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [permissionModalType, setPermissionModalType] = useState<'microphone' | 'camera' | 'both'>('microphone');
  const [instructionModalVisible, setInstructionModalVisible] = useState(false);
  const [instructionDeviceName, setInstructionDeviceName] = useState('');
  const [instructionText, setInstructionText] = useState('');
  const [createdProcessorList, setCreatedProcessorList] = useState<Processor[]>([]);
  const [recordingStatus, setRecordingStatus] = useState<'' | RecordingStatus>(
    recordingClient?.getCloudRecordingStatus() || ''
  );
  const [poseDetectionStatus, setPoseDetectionStatus] = useState<PoseDetectionStatus | ''>(
    PoseDetectionStatus.Disabled
  );
  const [galleryViewStatus, setGalleryViewStatus] = useState<GalleryViewStatus | ''>(GalleryViewStatus.Disabled);
  const [fullScreenStatus, setFullScreenStatus] = useState<FullScreenStatus | ''>(FullScreenStatus.FullScreen);
  const [recordingIsoStatus, setRecordingIsoStatus] = useState<'' | RecordingStatus>('');
  const [liveStreamVisible, setLiveStreamVisible] = useState(false);
  const [liveStreamStatus, setLiveStreamStatus] = useState(liveStreamClient?.getLiveStreamStatus());
  const history = useHistory();
  // Video Mask
  const [videoMaskVisible, setVideoMaskVisible] = useState(false);

  const [isSecondaryAudioStarted, setIsSecondaryAudioStarted] = useState(false);
  const [secondaryMicForm] = Form.useForm();

  const [isLeaveConfirmVisible, setIsLeaveConfirmVisible] = useState(false);
  const [isEndConfirmVisible, setIsEndConfirmVisible] = useState(false);

  const checkPermissions = useCallback(async () => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          console.log('Microphone permission state:', micPermission.state);
          setHasMicrophonePermission(
            micPermission.state === 'granted' ? true : micPermission.state === 'denied' ? false : null
          );
          micPermission.onchange = () => {
            console.log('Microphone permission changed to:', micPermission.state);
            setHasMicrophonePermission(
              micPermission.state === 'granted' ? true : micPermission.state === 'denied' ? false : null
            );
          };
        } catch (e) {
          console.warn('Microphone permission query not supported:', e);
        }
      }

      if (navigator.permissions && navigator.permissions.query) {
        try {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
          console.log('Camera permission state:', cameraPermission.state);
          setHasCameraPermission(
            cameraPermission.state === 'granted' ? true : cameraPermission.state === 'denied' ? false : null
          );
          cameraPermission.onchange = () => {
            console.log('Camera permission changed to:', cameraPermission.state);
            setHasCameraPermission(
              cameraPermission.state === 'granted' ? true : cameraPermission.state === 'denied' ? false : null
            );
          };
        } catch (e) {
          console.warn('Camera permission query not supported:', e);
        }
      }
    } catch (error) {
      console.warn('Error checking permissions:', error);
    }
  }, []);

  useMount(() => {
    checkPermissions();
  });
  const viewContext = useContext(ViewContext);
  const currentRole = zmClient.getCurrentUserInfo()?.userIdentity?.slice(0, 1);
  const participants = zmClient.getAllUser();
  const [startingRecording, setStartingRecording] = useState(false);
  const [poseDetectionLoading, setPoseDetectionLoading] = useState(false);
  const [poseDetectionStarted, setPoseDetectionStarted] = useState(false);
  const [whoEnablePoseDetection, setWhoEnablePoseDetection] = useState(0);
  const isInstructor = currentRole === UserRole.Instructor;

  const setMainViewForContext = () => {
    if (currentRole === UserRole.Student) {
      const userWithRoleInstructor = participants.find(
        (user) => user.userIdentity?.slice(0, 1) === UserRole.Instructor
      );
      if (userWithRoleInstructor) {
        console.log('userWithRoleInstructor', userWithRoleInstructor);
        viewContext.setMainViewUser(userWithRoleInstructor);
      }
    } else {
      const userWithRoleStudent = participants.find((user) => user.userIdentity?.slice(0, 1) === UserRole.Student);
      if (userWithRoleStudent) {
        // console.log('userWithRoleStudent', userWithRoleStudent);
        viewContext.setMainViewUser(userWithRoleStudent);
      }
    }
  };

  // Check if there is both host and non-host in participants
  const hasRequiredParticipants = useCallback(() => {
    const hasHost = participants.some((user) => user.isHost === true);
    const hasNonHost = participants.some((user) => user.isHost === false);
    return hasHost && hasNonHost;
  }, [participants]);

  // Auto-deactivate buttons when participants change and conditions are not met
  useEffect(() => {
    if (!hasRequiredParticipants()) {
      let shouldShowMessage = false;

      // If pose detection is currently enabled, disable it
      if (poseDetectionStatus === PoseDetectionStatus.Enabled) {
        setPoseDetectionStatus(PoseDetectionStatus.Disabled);
        setPoseDetectionLoading(false);
        setPoseDetectionStarted(false);

        // Remove active pose detection processor if it exists
        if (activeProcessor && activeProcessor.name === ProcessorName.PoseDetection) {
          mediaStream?.removeProcessor(activeProcessor);
          setActiveProcessor(undefined);
        }

        // Send command to stop pose detection if it's running
        const userWithStudentRole = zmClient
          .getAllUser()
          .find((user) => user.userIdentity?.slice(0, 1) === UserRole.Student);
        if (userWithStudentRole) {
          console.log('Sending command to stop pose detection for user:', userWithStudentRole.userId);
          cmdClient?.send(`/pose-detection-stop`, userWithStudentRole.userId);
        }

        shouldShowMessage = true;
      }

      // If gallery view is currently enabled, disable it
      if (galleryViewStatus === GalleryViewStatus.Enabled) {
        setGalleryViewStatus(GalleryViewStatus.Disabled);
        cmdClient?.send(`/gallery-view-disable`);
        viewContext.setGalleryView(false);
        viewContext.setSpeakerView(true);
        setMainViewForContext();
        shouldShowMessage = true;
      }

      // Show notification if any feature was auto-disabled
      if (shouldShowMessage) {
        message.warning('Some features have been automatically disabled due to insufficient participants');
      }
    }
  }, [
    hasRequiredParticipants,
    poseDetectionStatus,
    galleryViewStatus,
    zmClient,
    cmdClient,
    viewContext,
    setMainViewForContext,
    activeProcessor,
    mediaStream
  ]);

  useParticipantsChange(zmClient, () => {
    const currentUser = zmClient.getCurrentUserInfo();
    // setIsMuted(!!currentUser?.muted);
    setIsStartedVideo(!!currentUser?.bVideoOn);

    // Ensure audio is started when participants change (for hearing others)
    if (!isStartedAudio && mediaStream) {
      autoStartAudio().catch((e) => console.warn('Failed to auto start audio on participant change:', e));
    }
  });
  const onCameraClick = useCallback(async () => {
    if (isStartedVideo) {
      await mediaStream?.stopVideo();
      if (activePlaybackUrl) {
        await mediaStream?.switchMicrophone('default');
        setActiveMicrophone(mediaStream?.getActiveMicrophone());
        setActivePlaybackUrl('');
      }
    } else {
      if (hasCameraPermission === false) {
        setPermissionModalType('camera');
        setPermissionModalVisible(true);
        return;
      }

      if (hasCameraPermission === null && navigator.permissions && navigator.permissions.query) {
        try {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
          if (cameraPermission.state === 'denied') {
            setHasCameraPermission(false);
            setPermissionModalType('camera');
            setPermissionModalVisible(true);
            return;
          }
        } catch (e) {
          console.warn('Could not check camera permission:', e);
        }
      }

      try {
        const startVideoOptions = {
          hd: true,
          fullHd: true,
          ptz: mediaStream?.isBrowserSupportPTZ(),
          originalRatio: true
        };
        if (mediaStream?.isSupportVirtualBackground() && isBlur) {
          Object.assign(startVideoOptions, { virtualBackground: { imageUrl: 'blur' } });
        }
        await mediaStream?.startVideo(startVideoOptions);
        setHasCameraPermission(true);
      } catch (e: any) {
        if (
          e.name === 'NotAllowedError' ||
          e.name === 'PermissionDeniedError' ||
          e.type === 'INSUFFICIENT_PRIVILEGES'
        ) {
          setHasCameraPermission(false);
          setPermissionModalType('camera');
          setPermissionModalVisible(true);
        }
        console.warn('Camera start error:', e);
      }
    }
  }, [mediaStream, isStartedVideo, isBlur, activePlaybackUrl, hasCameraPermission]);

  const autoStartVideo = async () => {
    if (hasCameraPermission === false) {
      console.log('Camera permission denied, skipping auto start video');
      return;
    }

    if (initialIsStartedVideo === false) {
      console.log('Preview requested camera off; skip auto start video');
      return;
    }

    try {
      const startVideoOptions = {
        hd: true,
        fullHd: true,
        ptz: mediaStream?.isBrowserSupportPTZ(),
        originalRatio: true
      };
      if (mediaStream?.isSupportVirtualBackground() && isBlur) {
        Object.assign(startVideoOptions, { virtualBackground: { imageUrl: 'blur' } });
      }
      await mediaStream?.startVideo(startVideoOptions);
      setHasCameraPermission(true);
      // await onBlurBackground();
    } catch (e: any) {
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError' || e.type === 'INSUFFICIENT_PRIVILEGES') {
        setHasCameraPermission(false);
        console.warn('Camera permission denied:', e);
      } else {
        console.warn('Failed to auto start video:', e);
      }
    }
  };

  const autoStartAudio = async () => {
    // console.log('isMuted', isMuted);

    if (hasMicrophonePermission === false) {
      console.log('Microphone permission denied, skipping auto start audio');
      return;
    }

    if (requestedAudioOn === false) {
      console.log('Preview requested microphone off; skip auto start audio');
      return;
    }

    if (!isStartedAudio) {
      try {
        if (activePlaybackUrl) {
          await mediaStream?.startAudio({ mediaFile: { url: activePlaybackUrl, loop: true } });
        } else {
          await mediaStream?.startAudio({ highBitrate: true });
        }
        setActiveMicrophone(mediaStream?.getActiveMicrophone());
        setIsStartedAudio(true);
        setHasMicrophonePermission(true);

        // Preserve mute state from preview when joining the meeting
        if (isMuted) {
          await mediaStream?.muteAudio();
        }

        // Ensure speaker is set for audio output
        const currentSpeaker = mediaStream?.getActiveSpeaker();
        const speakerList = mediaStream?.getSpeakerList();
        if (!currentSpeaker && speakerList && speakerList.length > 0) {
          const defaultSpeaker = speakerList[0];
          await mediaStream?.switchSpeaker(defaultSpeaker.deviceId);
          setActiveSpeaker(mediaStream?.getActiveSpeaker());
        }
      } catch (e: any) {
        if (e?.errorCode === 6015 || e?.reason === 'no audio joined' || e?.type === 'INVALID_OPERATION') {
          console.warn('Auto start audio failed because no audio joined, retrying once:', e);
          try {
            if (activePlaybackUrl) {
              await mediaStream?.startAudio({ mediaFile: { url: activePlaybackUrl, loop: true } });
            } else {
              await mediaStream?.startAudio({ highBitrate: true });
            }
            setActiveMicrophone(mediaStream?.getActiveMicrophone());
            setIsStartedAudio(true);
            setHasMicrophonePermission(true);
            if (isMuted) {
              await mediaStream?.muteAudio();
            }
          } catch (retryError) {
            console.warn('Retry auto start audio failed:', retryError);
          }
        } else if (e.type === 'INSUFFICIENT_PRIVILEGES' && e.reason === 'USER_FORBIDDEN_MICROPHONE') {
          setIsMicrophoneForbidden(true);
          setHasMicrophonePermission(false);
        } else if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
          setHasMicrophonePermission(false);
          console.warn('Microphone permission denied:', e);
        } else {
          console.warn('Failed to auto start audio:', e);
        }
      }
    }
  };

  useEffect(() => {
    // Delay auto start to ensure mediaStream is ready
    const timer = setTimeout(() => {
      autoStartAudio();
      autoStartVideo();
      onMirrorVideo();
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  // Add user interaction handler to retry audio start in production
  useEffect(() => {
    const handleUserInteraction = async () => {
      if (!isStartedAudio && mediaStream) {
        try {
          await autoStartAudio();
        } catch (e) {
          console.warn('Failed to auto start audio on user interaction:', e);
        }
      }
    };

    // Listen for user interactions to retry audio start
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, [isStartedAudio, mediaStream]);

  // Ensure audio is started when mediaStream becomes available
  useEffect(() => {
    if (mediaStream && !isStartedAudio) {
      const timer = setTimeout(() => {
        autoStartAudio().catch((e) => console.warn('Failed to auto start audio when mediaStream ready:', e));
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [mediaStream, isStartedAudio]);

  const requestPermission = useCallback(
    async (type: 'microphone' | 'camera' | 'both') => {
      console.log('Requesting permission for:', type);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('getUserMedia is not supported in this browser');
        alert(
          'Your browser does not support media access. Please use a modern browser like Chrome, Firefox, or Safari.'
        );
        return;
      }

      setPermissionModalVisible(false);

      try {
        const constraints: MediaStreamConstraints = {};
        if (type === 'microphone' || type === 'both') {
          constraints.audio = true;
        }
        if (type === 'camera' || type === 'both') {
          constraints.video = true;
        }

        console.log('Calling getUserMedia with constraints:', constraints);
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        console.log('Permission granted, stream received');

        stream.getTracks().forEach((track) => {
          track.stop();
          console.log('Track stopped:', track.kind);
        });

        if (type === 'microphone' || type === 'both') {
          setHasMicrophonePermission(true);
        }
        if (type === 'camera' || type === 'both') {
          setHasCameraPermission(true);
        }

        if (type === 'microphone' || type === 'both') {
          if (!isStartedAudio) {
            try {
              if (activePlaybackUrl) {
                await mediaStream?.startAudio({ mediaFile: { url: activePlaybackUrl, loop: true } });
              } else {
                await mediaStream?.startAudio({ highBitrate: true });
              }
              setActiveMicrophone(mediaStream?.getActiveMicrophone());
              setIsStartedAudio(true);
            } catch (e) {
              console.warn('Failed to start audio after permission granted:', e);
            }
          }
        }
        if (type === 'camera' || type === 'both') {
          if (!isStartedVideo) {
            try {
              const startVideoOptions = {
                hd: true,
                fullHd: true,
                ptz: mediaStream?.isBrowserSupportPTZ(),
                originalRatio: true
              };
              if (mediaStream?.isSupportVirtualBackground() && isBlur) {
                Object.assign(startVideoOptions, { virtualBackground: { imageUrl: 'blur' } });
              }
              await mediaStream?.startVideo(startVideoOptions);
              setIsStartedVideo(true);
            } catch (e) {
              console.warn('Failed to start video after permission granted:', e);
            }
          }
        }
      } catch (error: any) {
        console.error('Permission request failed:', error);
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          const deviceName =
            type === 'microphone' ? 'microphone' : type === 'camera' ? 'camera' : 'microphone and camera';
          const instructions = getBrowserPermissionInstructions(deviceName);
          setInstructionDeviceName(deviceName);
          setInstructionText(instructions);
          setInstructionModalVisible(true);

          if (type === 'microphone' || type === 'both') {
            setHasMicrophonePermission(false);
          }
          if (type === 'camera' || type === 'both') {
            setHasCameraPermission(false);
          }
        }
      }
    },
    [mediaStream, isStartedAudio, isStartedVideo, isBlur, activePlaybackUrl]
  );

  const startLocalAudio = useCallback(async () => {
    if (!mediaStream) return;
    if (activePlaybackUrl) {
      await mediaStream.startAudio({ mediaFile: { url: activePlaybackUrl, loop: true } });
    } else {
      await mediaStream.startAudio({ highBitrate: true });
    }
    setActiveMicrophone(mediaStream.getActiveMicrophone());
    setIsStartedAudio(true);
    setHasMicrophonePermission(true);
    setAudio('computer');
  }, [mediaStream, activePlaybackUrl]);

  const onMicrophoneClick = useCallback(async () => {
    const audioJoined = Boolean(mediaStream?.getActiveMicrophone());

    if (isStartedAudio && !audioJoined) {
      if (isMuted) {
        try {
          await startLocalAudio();
          await mediaStream?.unmuteAudio();
          setIsMuted(false);
          return;
        } catch (e: any) {
          console.warn('Failed to start audio for muted state:', e);
        }
      } else {
        setIsStartedAudio(false);
        setIsMuted(true);
        return;
      }
    }

    if (isStartedAudio) {
      if (isMuted) {
        try {
          await mediaStream?.unmuteAudio();
          setIsMuted(false);
        } catch (e: any) {
          console.warn('Audio unmute failed, retrying startAudio:', e);
          try {
            await startLocalAudio();
            await mediaStream?.unmuteAudio();
            setIsMuted(false);
          } catch (fallbackError) {
            console.warn('Failed to start audio after unmute fallback:', fallbackError);
            // if (
            //   fallbackError?.errorCode === 6015 ||
            //   fallbackError?.reason === 'no audio joined' ||
            //   fallbackError?.type === 'INVALID_OPERATION'
            // ) {
            //   console.warn('Retry startAudio also failed due to no audio joined:', fallbackError);
            // } else {
            //   console.warn('Failed to start audio after unmute fallback:', fallbackError);
            // }
          }
        }
      } else {
        try {
          await mediaStream?.muteAudio();
          setIsMuted(true);
        } catch (e: any) {
          if (e?.errorCode === 6015 || e?.reason === 'no audio joined' || e?.type === 'INVALID_OPERATION') {
            console.warn('Mute called without joined audio, aligning UI state:', e);
            setIsStartedAudio(false);
            setIsMuted(true);
          } else {
            console.warn('Failed to mute audio:', e);
          }
        }
      }
    } else {
      if (hasMicrophonePermission === false) {
        setPermissionModalType('microphone');
        setPermissionModalVisible(true);
        return;
      }

      if (hasMicrophonePermission === null && navigator.permissions && navigator.permissions.query) {
        try {
          const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          if (micPermission.state === 'denied') {
            setHasMicrophonePermission(false);
            setPermissionModalType('microphone');
            setPermissionModalVisible(true);
            return;
          }
        } catch (e) {
          console.warn('Could not check microphone permission:', e);
        }
      }

      try {
        await startLocalAudio();
        setIsMuted(false);
      } catch (e: any) {
        if (e.type === 'INSUFFICIENT_PRIVILEGES' && e.reason === 'USER_FORBIDDEN_MICROPHONE') {
          setIsMicrophoneForbidden(true);
          setHasMicrophonePermission(false);
          setPermissionModalType('microphone');
          setPermissionModalVisible(true);
        } else if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
          setHasMicrophonePermission(false);
          setPermissionModalType('microphone');
          setPermissionModalVisible(true);
        }
        console.warn(e);
      }
    }
  }, [mediaStream, isStartedAudio, isMuted, activePlaybackUrl, hasMicrophonePermission, startLocalAudio]);
  const onMicrophoneMenuClick = async (key: string) => {
    if (mediaStream) {
      const [type, deviceId] = key.split('|');
      if (type === 'microphone') {
        if (deviceId !== activeMicrophone) {
          await mediaStream.switchMicrophone(deviceId);
          setActiveMicrophone(mediaStream.getActiveMicrophone());
        }
      } else if (type === 'speaker') {
        if (deviceId !== activeSpeaker) {
          await mediaStream.switchSpeaker(deviceId);
          setActiveSpeaker(mediaStream.getActiveSpeaker());
        }
      } else if (type === 'leave audio') {
        if (audio === 'computer') {
          await mediaStream.stopAudio();
          setIsMicrophoneForbidden(false);
        } else if (audio === 'phone') {
          await mediaStream.hangup();
          setPhoneCallStatus(undefined);
        }
      } else if (type === 'statistic') {
        setSelectedStatisticTab('audio');
        setStatisticVisible(true);
      } else if (type === 'secondary audio') {
        if (isSecondaryAudioStarted) {
          await mediaStream.stopSecondaryAudio();
          setIsSecondaryAudioStarted(false);
        } else {
          const selectedMic = secondaryMicForm.getFieldValue('mic');
          if (!mediaStream.getMicList().some((item) => item.deviceId === selectedMic)) {
            secondaryMicForm.setFieldValue('mic', undefined);
          }
          Modal.confirm({
            title: 'Start secondary audio',
            content: (
              <Form form={secondaryMicForm}>
                <Form.Item label="Microphone" name="mic" required>
                  <Select
                    options={mediaStream.getMicList().map((item) => ({
                      value: item.deviceId,
                      label: item.label,
                      disabled:
                        item.deviceId === mediaStream.getActiveMicrophone() ||
                        item.label ===
                          mediaStream.getMicList()?.find((item) => item.deviceId === mediaStream.getActiveMicrophone())
                            ?.label
                    }))}
                  />
                </Form.Item>
                <Form.Item label="Contraintes" name="constraints">
                  <Checkbox.Group
                    options={[
                      { label: 'AGC', value: 'autoGainControl' },
                      {
                        label: 'ANC',
                        value: 'noiseSuppression'
                      },
                      {
                        label: 'AEC',
                        value: 'echoCancellation'
                      }
                    ]}
                  />
                </Form.Item>
              </Form>
            ),
            onOk: async () => {
              try {
                const data = await secondaryMicForm.validateFields();
                const { mic, constraints } = data;
                const option = { autoGainControl: false, noiseSuppression: false, echoCancellation: false };
                if (constraints) {
                  constraints.forEach((key: string) => {
                    Object.assign(option, { [`${key}`]: true });
                  });
                }
                await mediaStream.startSecondaryAudio(mic, option);
                setIsSecondaryAudioStarted(true);
              } catch (e) {
                console.warn(e);
              }
            }
          });
        }
      }
    }
  };
  const onSwitchCamera = async (key: string) => {
    if (mediaStream) {
      if (activeCamera !== key) {
        await mediaStream.switchCamera(key);
        setActiveCamera(mediaStream.getActiveCamera());
        if (activePlaybackUrl) {
          await mediaStream.switchMicrophone('default');
          setActiveMicrophone(mediaStream.getActiveMicrophone());
          setActivePlaybackUrl('');
        }
      }
    }
  };
  const onMirrorVideo = async () => {
    await mediaStream?.mirrorVideo(!isMirrored);
    setIsMirrored(!isMirrored);
  };
  const onBlurBackground = async () => {
    const isSupportVirtualBackground = mediaStream?.isSupportVirtualBackground();
    if (isSupportVirtualBackground) {
      if (isBlur) {
        await mediaStream?.updateVirtualBackgroundImage(undefined);
      } else {
        await mediaStream?.updateVirtualBackgroundImage(location.origin + '/background.jpeg');
      }
    } else {
      setVideoMaskVisible(true);
    }
    setIsBlur(!isBlur);
  };
  const onPhoneCall = async (code: string, phoneNumber: string, name: string, option: DialOutOption) => {
    await mediaStream?.inviteByPhone(code, phoneNumber, name, option);
  };
  const onPhoneCallCancel = async (code: string, phoneNumber: string, option: { callMe: boolean }) => {
    if ([DialoutState.Calling, DialoutState.Ringing, DialoutState.Accepted].includes(phoneCallStatus as any)) {
      await mediaStream?.cancelInviteByPhone(code, phoneNumber, option);
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 3000);
      });
    }
    return Promise.resolve();
  };
  const onHostAudioMuted = useCallback(
    (payload: any) => {
      const { action, source, type } = payload;
      const currentUser = zmClient.getCurrentUserInfo();
      setIsStartedAudio(currentUser.audio === 'computer' || currentUser.audio === 'phone');
      setAudio(currentUser.audio);
      if (action === AudioChangeAction.Join) {
        setIsStartedAudio(true);
        setAudio(type);
      } else if (action === AudioChangeAction.Leave) {
        setIsStartedAudio(false);
      } else if (action === AudioChangeAction.Muted) {
        if (source === MutedSource.PassiveByMuteOne) {
          message.info('Host muted you');
        }
      } else if (action === AudioChangeAction.Unmuted) {
        if (source === 'passive') {
          message.info('Host unmuted you');
        }
      }
    },
    [zmClient]
  );
  const onScreenShareClick = useCallback(async () => {
    if (mediaStream?.getShareStatus() === ShareStatus.End && selfShareCanvas) {
      await mediaStream?.startShareScreen(selfShareCanvas, { requestReadReceipt: true });
    }
  }, [mediaStream, selfShareCanvas]);

  const onLiveTranscriptionClick = useCallback(async () => {
    if (isDisableCaptions) {
      message.info('Captions has been disable by host.');
    } else if (isStartedLiveTranscription) {
      message.info('Live transcription has started.');
    } else if (!isStartedLiveTranscription) {
      await liveTranscriptionClient?.startLiveTranscription();
      setIsStartedLiveTranscription(true);
    }
  }, [isStartedLiveTranscription, isDisableCaptions, liveTranscriptionClient]);

  const onDisableCaptions = useCallback(
    async (disable: boolean) => {
      if (disable && !isDisableCaptions) {
        await liveTranscriptionClient?.disableCaptions(disable);
        setIsStartedLiveTranscription(false);
        setIsDisableCaptions(true);
      } else if (!disable && isDisableCaptions) {
        await liveTranscriptionClient?.disableCaptions(disable);
        setIsDisableCaptions(false);
      }
    },
    [isDisableCaptions, liveTranscriptionClient]
  );

  const onLeaveClick = useCallback(async () => {
    const isHost = zmClient.isHost();
    localStorage.setItem('isHost', JSON.stringify(isHost));

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (e) {
      console.warn('Failed to exit fullscreen on leave:', e);
    }

    try {
      if (isStartedVideo) {
        await mediaStream?.stopVideo();
      }
      if (isStartedAudio) {
        await mediaStream?.stopAudio();
      }
    } catch (e) {
      console.warn('Failed to stop media before leaving:', e);
    }

    await zmClient.leave();
    history.push(`/leave/${slotBookingId}/${userId}`);
  }, [zmClient, history, slotBookingId, userId, isStartedVideo, isStartedAudio, mediaStream]);

  const onEndClick = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (e) {
      console.warn('Failed to exit fullscreen on end:', e);
    }

    try {
      if (isStartedVideo) {
        await mediaStream?.stopVideo();
      }
      if (isStartedAudio) {
        await mediaStream?.stopAudio();
      }
    } catch (e) {
      console.warn('Failed to stop media before ending:', e);
    }

    await zmClient.leave(true);
    history.push('/');
  }, [zmClient, isStartedVideo, isStartedAudio, mediaStream]);

  const handleLeaveClick = useCallback(() => {
    setIsLeaveConfirmVisible(true);
  }, []);

  const handleEndClick = useCallback(() => {
    setIsEndConfirmVisible(true);
  }, []);

  const onPassivelyStopShare = useCallback(({ reason }: any) => {
    console.log('passively stop reason:', reason);
  }, []);
  const onDeviceChange = useCallback(() => {
    if (mediaStream) {
      setMicList(mediaStream.getMicList());
      setSpeakerList(mediaStream.getSpeakerList());
      if (!isAndroidOrIOSBrowser()) {
        setCameraList(mediaStream.getCameraList());
      }
      setActiveMicrophone(mediaStream.getActiveMicrophone());
      setActiveSpeaker(mediaStream.getActiveSpeaker());
      setActiveCamera(mediaStream.getActiveCamera());
    }
  }, [mediaStream]);

  const onRecordingChange = useCallback(() => {
    setRecordingStatus(recordingClient?.getCloudRecordingStatus() || '');
  }, [recordingClient]);

  const onRecordingISOChange = useCallback(
    (payload: any) => {
      if (payload?.userId === zmClient.getSessionInfo().userId || payload?.status === RecordingStatus.Ask) {
        setRecordingIsoStatus(payload?.status);
      }
      console.log('recording-iso-change', payload);
    },
    [zmClient]
  );

  const onDialOutChange = useCallback((payload: any) => {
    setPhoneCallStatus(payload.code);
  }, []);

  useEffect(() => {
    const subscriptionStatus = localStorage.getItem('subscriptionStatus');
    if (subscriptionStatus === 'UNSUBSCRIBED' && isInstructor) {
      onRecordingClick('Record');
    }
    localStorage.removeItem('subscriptionStatus');
  }, []);

  const onRecordingClick = async (key: string) => {
    console.log('onRecordingClick', key);
    switch (key) {
      case 'Record': {
        setStartingRecording(true);
        await recordingClient?.startCloudRecording();
        setStartingRecording(false);
        break;
      }
      case 'Resume': {
        await recordingClient?.resumeCloudRecording();
        break;
      }
      case 'Stop': {
        await recordingClient?.stopCloudRecording();
        break;
      }
      case 'Pause': {
        await recordingClient?.pauseCloudRecording();
        break;
      }
      case 'Status': {
        break;
      }
      default: {
        await recordingClient?.startCloudRecording();
      }
    }
  };
  const onShareAudioChange = useCallback(
    (payload: any) => {
      const { state } = payload;
      if (state === 'on') {
        if (!mediaStream?.isSupportMicrophoneAndShareAudioSimultaneously()) {
          setIsComputerAudioDisabled(true);
        }
      } else if (state === 'off') {
        setIsComputerAudioDisabled(false);
      }
    },
    [mediaStream]
  );
  const onHostAskToUnmute = useCallback(
    (payload: any) => {
      const { reason } = payload;
      Modal.confirm({
        title: 'Unmute audio',
        content: `Host ask to unmute audio, reason: ${reason}`,
        onOk: async () => {
          try {
            await mediaStream?.unmuteAudio();
          } catch (e: any) {
            if (e?.errorCode === 6015 || e?.reason === 'no audio joined' || e?.type === 'INVALID_OPERATION') {
              console.warn('Host-triggered unmute failed because audio is not joined, retrying startAudio:', e);
              try {
                if (activePlaybackUrl) {
                  await mediaStream?.startAudio({ mediaFile: { url: activePlaybackUrl, loop: true } });
                } else {
                  await mediaStream?.startAudio({ highBitrate: true });
                }
                setActiveMicrophone(mediaStream?.getActiveMicrophone());
                setIsStartedAudio(true);
                setHasMicrophonePermission(true);
                await mediaStream?.unmuteAudio();
              } catch (fallbackError) {
                console.warn('Failed to recover audio after host unmute fallback:', fallbackError);
              }
            } else {
              console.warn('Failed to unmute audio from host request:', e);
            }
          }
        },
        onCancel() {
          console.log('cancel');
        }
      });
    },
    [mediaStream, activePlaybackUrl]
  );

  const onCaptionStatusChange = useCallback((payload: any) => {
    const { autoCaption } = payload;
    if (autoCaption) {
      message.info('Auto live transcription enabled!');
    }
  }, []);

  const onCaptionMessage = useCallback((payload: any) => {
    const { text, done } = payload;
    setCaption({
      text,
      isOver: done
    });
  }, []);

  const onCaptionDisable = useCallback((payload: any) => {
    setIsDisableCaptions(payload);
    if (payload) {
      setIsStartedLiveTranscription(false);
    }
  }, []);

  const onCanSeeMyScreen = useCallback(() => {
    message.info('Users can now see your screen', 1);
  }, []);
  const onSelectVideoPlayback = useCallback(
    async (url: string) => {
      if (activePlaybackUrl !== url) {
        await mediaStream?.switchCamera({ url, loop: true });
        if (isStartedAudio) {
          await mediaStream?.switchMicrophone({ url, loop: true });
        } else {
          await mediaStream?.startAudio({ mediaFile: { url, loop: true } });
        }
        setActivePlaybackUrl(url);
      }
    },
    [isStartedAudio, activePlaybackUrl, mediaStream]
  );

  const onLiveStreamClick = useCallback(() => {
    if (liveStreamStatus === LiveStreamStatus.Ended) {
      setLiveStreamVisible(true);
    } else if (liveStreamStatus === LiveStreamStatus.InProgress) {
      liveStreamClient?.stopLiveStream();
    }
  }, [liveStreamStatus, liveStreamClient]);
  const onLiveStreamStatusChange = useCallback((status: any) => {
    setLiveStreamStatus(status);
    if (status === LiveStreamStatus.Timeout) {
      message.error('Start live streaming timeout');
    }
  }, []);
  const updateWatermarkImage = useCallback((processor: Processor, imageUrl: string) => {
    const img = document.createElement('img');
    img.width = 640;
    img.height = 360;
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    img.onload = () => {
      createImageBitmap(img).then((ibm) => {
        processor?.port?.postMessage({
          cmd: 'update_watermark_image',
          data: ibm
        });
      });
    };
  }, []);
  const onProcessorClick = useCallback(
    async (processor: any) => {
      let tempProcessor = createdProcessorList?.find((p) => p.name === processor.name);
      console.log('createdProcessorList: ', createdProcessorList);
      console.log('tempProcessor: ', tempProcessor);
      console.log('activeProcessor: ', activeProcessor);

      if (!tempProcessor) {
        try {
          tempProcessor = await mediaStream?.createProcessor(processor);

          console.log('tempProcessor created: ', tempProcessor);
          setCreatedProcessorList([...createdProcessorList, tempProcessor as Processor]);
        } catch (e) {
          console.log(e);
        }

        if (activeProcessor) {
          if (processor.name === activeProcessor?.name) {
            mediaStream?.removeProcessor(activeProcessor);
            setActiveProcessor(undefined);
            if (whoEnablePoseDetection) {
              console.log('Sending command to stop pose detection instructor:', whoEnablePoseDetection);
              cmdClient?.send(`/pose-detection-student-stop`);
            }
          } else {
            mediaStream?.removeProcessor(activeProcessor);
            mediaStream?.addProcessor(tempProcessor as Processor);
            setActiveProcessor(tempProcessor as Processor);
            cmdClient?.send(`/pose-detection-student-start`);
          }
        } else {
          try {
            await mediaStream?.addProcessor(tempProcessor as Processor);
            setActiveProcessor(tempProcessor as Processor);
            cmdClient?.send(`/pose-detection-student-start`);
          } catch (e) {
            console.error(e);
          }
        }
      } else {
        if (activeProcessor) {
          if (processor.name === activeProcessor?.name) {
            mediaStream?.removeProcessor(activeProcessor);
            setActiveProcessor(undefined);
            if (whoEnablePoseDetection) {
              console.log('Sending command to stop pose detection instructor:', whoEnablePoseDetection);
              cmdClient?.send(`/pose-detection-student-stop`);
            }
          } else {
            mediaStream?.removeProcessor(activeProcessor);
            mediaStream?.addProcessor(tempProcessor as Processor);
            setActiveProcessor(tempProcessor as Processor);
            cmdClient?.send(`/pose-detection-student-start`);
          }
        } else {
          try {
            await mediaStream?.addProcessor(tempProcessor as Processor);
            setActiveProcessor(tempProcessor as Processor);
            cmdClient?.send(`/pose-detection-student-start`);
          } catch (e) {
            console.error(e);
          }
        }
      }
    },
    [createdProcessorList, activeProcessor]
  );
  const onVideoScreenshotTaken = useCallback((payload: any) => {
    const { displayName, userId } = payload;
    message.info(`${displayName}(User:${userId}) just took a screenshot of your video`);
  }, []);
  const onShareViewScreenshotTaken = useCallback((payload: any) => {
    const { displayName, userId } = payload;
    message.info(`${displayName}(User:${userId}) just took a screenshot of your sharing`);
  }, []);
  const onVideoDetailDataChange = useCallback((payload: any) => {
    // console.log(`User ${payload.userId} is aspectRatio at ${payload.aspectRatio}`);
  }, []);
  useEffect(() => {
    zmClient.on('current-audio-change', onHostAudioMuted);
    zmClient.on('passively-stop-share', onPassivelyStopShare);
    zmClient.on('device-change', onDeviceChange);
    zmClient.on('recording-change', onRecordingChange);
    zmClient.on('individual-recording-change', onRecordingISOChange);
    zmClient.on('dialout-state-change', onDialOutChange);
    zmClient.on('share-audio-change', onShareAudioChange);
    zmClient.on('host-ask-unmute-audio', onHostAskToUnmute);
    zmClient.on('caption-status', onCaptionStatusChange);
    zmClient.on('caption-message', onCaptionMessage);
    zmClient.on('caption-host-disable', onCaptionDisable);
    zmClient.on('share-can-see-screen', onCanSeeMyScreen);
    zmClient.on('live-stream-status', onLiveStreamStatusChange);
    zmClient.on('video-screenshot-taken', onVideoScreenshotTaken);
    zmClient.on('share-content-screenshot-taken', onShareViewScreenshotTaken);
    zmClient.on('video-detailed-data-change', onVideoDetailDataChange);
    return () => {
      zmClient.off('current-audio-change', onHostAudioMuted);
      zmClient.off('passively-stop-share', onPassivelyStopShare);
      zmClient.off('device-change', onDeviceChange);
      zmClient.off('recording-change', onRecordingChange);
      zmClient.off('individual-recording-change', onRecordingISOChange);
      zmClient.off('dialout-state-change', onDialOutChange);
      zmClient.off('share-audio-change', onShareAudioChange);
      zmClient.off('host-ask-unmute-audio', onHostAskToUnmute);
      zmClient.off('caption-status', onCaptionStatusChange);
      zmClient.off('caption-message', onCaptionMessage);
      zmClient.off('caption-host-disable', onCaptionDisable);
      zmClient.off('share-can-see-screen', onCanSeeMyScreen);
      zmClient.off('live-stream-status', onLiveStreamStatusChange);
      zmClient.off('video-screenshot-taken', onVideoScreenshotTaken);
      zmClient.off('share-content-screenshot-taken', onShareViewScreenshotTaken);
      zmClient.off('video-aspect-ratio-change', onVideoDetailDataChange);
    };
  }, [
    zmClient,
    onHostAudioMuted,
    onPassivelyStopShare,
    onDeviceChange,
    onRecordingChange,
    onDialOutChange,
    onShareAudioChange,
    onHostAskToUnmute,
    onCaptionStatusChange,
    onCaptionMessage,
    onCanSeeMyScreen,
    onRecordingISOChange,
    onCaptionDisable,
    onLiveStreamStatusChange,
    onVideoScreenshotTaken,
    onShareViewScreenshotTaken,
    onVideoDetailDataChange
  ]);
  useUnmount(() => {
    if (zmClient.getSessionInfo().isInMeeting) {
      if (isStartedAudio) {
        mediaStream?.stopAudio().catch(() => {});
      }
      if (isStartedVideo) {
        mediaStream?.stopVideo().catch(() => {});
      }
      mediaStream?.stopShareScreen();
    }
  });
  useMount(() => {
    setCurrentUserId(zmClient.getSessionInfo().userId);
    if (mediaStream) {
      setIsSupportPhone(!!mediaStream.isSupportPhoneFeature());
      setPhoneCountryList(mediaStream.getSupportCountryInfo() || []);
      setSharePrivileg(mediaStream.getSharePrivilege());
      if (isAndroidOrIOSBrowser()) {
        setCameraList([
          { deviceId: MobileVideoFacingMode.User, label: 'Front-facing' },
          { deviceId: MobileVideoFacingMode.Environment, label: 'Rear-facing' }
        ]);
      }
    }
  });
  useEffect(() => {
    if (mediaStream) {
      console.log('Subscribing to video statistic data with detailed mode');
      mediaStream.subscribeVideoStatisticData({ detailed: true });
    }

    if (mediaStream && zmClient.getSessionInfo().isInMeeting && statisticVisible) {
      mediaStream.subscribeAudioStatisticData();
      mediaStream.subscribeVideoStatisticData();
      mediaStream.subscribeShareStatisticData();
    }
    return () => {
      if (zmClient.getSessionInfo().isInMeeting) {
        mediaStream?.unsubscribeAudioStatisticData();
        mediaStream?.unsubscribeVideoStatisticData();
        mediaStream?.unsubscribeShareStatisticData();
      }
    };
  }, [mediaStream, zmClient, statisticVisible]);

  const onPoseDetectionClick = useCallback(() => {
    if (!hasRequiredParticipants()) {
      message.error('Need at least one host and one participant to use this feature');
      return;
    }

    const userWithStudentRole = zmClient
      .getAllUser()
      .find((user) => user.userIdentity?.slice(0, 1) === UserRole.Student);
    if (!userWithStudentRole) {
      message.error('No student found');
      return;
    }
    if (poseDetectionStatus === PoseDetectionStatus.Disabled) {
      if (!poseDetectionStarted) {
        setPoseDetectionLoading(true);
      }

      setPoseDetectionStatus(PoseDetectionStatus.Enabled);
      cmdClient?.send(`/pose-detection-start`, userWithStudentRole.userId);
    } else {
      setPoseDetectionStatus(PoseDetectionStatus.Disabled);
      cmdClient?.send(`/pose-detection-stop`, userWithStudentRole.userId);
    }
  }, [poseDetectionStatus, zmClient, hasRequiredParticipants]);

  const recordingButtons: RecordButtonProps[] = getRecordingButtons(recordingStatus, isInstructor);
  const poseDetectionButton: PoseDetectionProps | null = getPoseDetectionButtons(poseDetectionStatus, isInstructor);
  const galleryViewButton: GalleryViewProps | null = getGalleryViewButtons(galleryViewStatus, isInstructor);
  const fullScreenButton: FullScreenProps | null = getFullScreenButtons(fullScreenStatus);
  const onGalleryViewClick = useCallback(() => {
    // Check if there is both host and non-host in participants
    if (!hasRequiredParticipants()) {
      message.error('Need at least one host and one participant to use this feature');
      return;
    }

    if (galleryViewStatus === GalleryViewStatus.Disabled) {
      cmdClient?.send(`/gallery-view`);
      viewContext.setGalleryView(true);
      viewContext.setSpeakerView(false);
      viewContext.setPinViewUser(null);
      viewContext.setMainViewUser(null);
      setGalleryViewStatus(GalleryViewStatus.Enabled);
    } else {
      cmdClient?.send(`/gallery-view-disable`);
      viewContext.setGalleryView(false);
      viewContext.setSpeakerView(true);
      setMainViewForContext();
      setGalleryViewStatus(GalleryViewStatus.Disabled);
    }
  }, [galleryViewStatus, hasRequiredParticipants]);
  const onFullScreenClick = useCallback(() => {
    if (fullScreenStatus === FullScreenStatus.FullScreen) {
      setFullScreenStatus(FullScreenStatus.ExitFullScreen);
      // Make browser exit full screen
      document.exitFullscreen();
    } else {
      // Make browser full screen
      document.documentElement.requestFullscreen();
      setFullScreenStatus(FullScreenStatus.FullScreen);
    }
  }, [fullScreenStatus]);

  // Listen for fullscreen changes (handles ESC key press)
  useEffect(() => {
    const handleFullScreenChange = () => {
      // Check if currently in fullscreen using multiple browser-compatible methods
      const isFullScreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );

      if (isFullScreen) {
        // Currently in fullscreen
        setFullScreenStatus(FullScreenStatus.FullScreen);
      } else {
        // Exited fullscreen (could be via ESC key or other methods)
        setFullScreenStatus(FullScreenStatus.ExitFullScreen);
      }
    };

    // Add event listeners for different browsers
    const events = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];

    events.forEach((event) => {
      document.addEventListener(event, handleFullScreenChange);
    });

    // Cleanup event listeners on component unmount
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleFullScreenChange);
      });
    };
  }, []);
  const onCommandMessage = async (payload: CommandChannelMsg) => {
    if (payload.text === '/pose-detection-start') {
      if (!poseDetectionStarted) {
        setPoseDetectionLoading(true);
      }

      console.log('pose-detection-start from user', payload.senderId, payload.senderName);
      setWhoEnablePoseDetection(payload.senderId);

      setPoseDetectionStatus(PoseDetectionStatus.Enabled);
      const poseProcessor = videoProcessor.find((p) => p.name === ProcessorName.PoseDetection);
      if (!poseProcessor) {
        message.error('Pose detection processor not found');
        return;
      }
      let tempProcessor = createdProcessorList?.find((p) => p.name === poseProcessor?.name);
      if (!tempProcessor) {
        try {
          tempProcessor = await mediaStream?.createProcessor(poseProcessor);
          setCreatedProcessorList([...createdProcessorList, tempProcessor as Processor]);
          if (tempProcessor && tempProcessor.port) {
            tempProcessor.port.onmessage = (e) => {
              const { cmd, message } = e.data;
              if (cmd === 'pose_ready') {
                zmClient.getCommandClient()?.send(`/pose-detection-started`);
                setPoseDetectionLoading(false);
                setPoseDetectionStarted(true);
              }
            };
          }
        } catch (e) {
          console.log(e);
        }
      }
      if (activeProcessor) {
        if (poseProcessor.name === activeProcessor?.name) {
          mediaStream?.removeProcessor(activeProcessor);
          setActiveProcessor(undefined);
        } else {
          mediaStream?.removeProcessor(activeProcessor);
          mediaStream?.addProcessor(tempProcessor as Processor);
          setActiveProcessor(tempProcessor as Processor);
        }
      } else {
        try {
          await mediaStream?.addProcessor(tempProcessor as Processor);
          setActiveProcessor(tempProcessor as Processor);
        } catch (e) {
          console.error(e);
        }
      }
    } else if (payload.text === '/pose-detection-stop') {
      setPoseDetectionLoading(false);
      setPoseDetectionStarted(true);
      setWhoEnablePoseDetection(0);
      console.log('pose-detection-stop');
      setPoseDetectionStatus(PoseDetectionStatus.Disabled);
      let tempProcessor = createdProcessorList?.find((p) => p.name === ProcessorName.PoseDetection);
      if (activeProcessor) {
        if (activeProcessor.name === ProcessorName.PoseDetection) {
          mediaStream?.removeProcessor(activeProcessor);
          setActiveProcessor(undefined);
        }
      }
    } else if (payload.text === '/pose-detection-student-start') {
      console.log('pose-detection-student-start');
      if (isInstructor && poseDetectionStatus === PoseDetectionStatus.Disabled) {
        setPoseDetectionStatus(PoseDetectionStatus.Enabled);
      }
    } else if (payload.text === '/pose-detection-student-stop') {
      console.log('pose-detection-student-stop');
      if (isInstructor && poseDetectionStatus === PoseDetectionStatus.Enabled) {
        setPoseDetectionStatus(PoseDetectionStatus.Disabled);
      }
    } else if (payload.text === '/pose-detection-started') {
      setPoseDetectionLoading(false);
      setPoseDetectionStarted(true);
    } else if (payload.text.startsWith('/spotlight-start_')) {
      viewContext.setGalleryView(true);
      viewContext.setSpeakerView(false);
      // Add delay
      viewContext.setPinViewUser(null);
      viewContext.setMainViewUser(null);
      setTimeout(() => {
        const userId = parseInt(payload.text.split('_')[1]);
        const spotlightUser = zmClient.getUser(userId);
        viewContext.setMainViewUser(spotlightUser);
        viewContext.setPinViewUser(spotlightUser);
        viewContext.setSpeakerView(true);
        viewContext.setGalleryView(false);
        console.log('spotlightUser', spotlightUser);
        console.log('viewContext', viewContext);
      }, 100);
    } else if (payload.text === '/spotlight-stop') {
      viewContext.setGalleryView(true);
      viewContext.setSpeakerView(false);
      // Add delay
      viewContext.setPinViewUser(null);
      viewContext.setMainViewUser(null);

      setTimeout(() => {
        viewContext.setSpeakerView(true);
        viewContext.setGalleryView(false);
        setMainViewForContext();
      }, 100);
    } else if (payload.text === '/gallery-view') {
      viewContext.setGalleryView(true);
      viewContext.setSpeakerView(false);
    } else if (payload.text === '/speaker-view') {
      viewContext.setSpeakerView(true);
      viewContext.setGalleryView(false);
    } else if (payload.text === '/gallery-view-disable') {
      viewContext.setGalleryView(true);
      viewContext.setSpeakerView(false);
      viewContext.setMainViewUser(null);
      viewContext.setPinViewUser(null);
      setTimeout(() => {
        viewContext.setSpeakerView(true);
        viewContext.setGalleryView(false);
        setMainViewForContext();
      }, 100);
    } else if (payload.text === '/speaker-view-disable') {
      viewContext.setSpeakerView(false);
      viewContext.setGalleryView(true);
      viewContext.setMainViewUser(null);
      viewContext.setPinViewUser(null);
    } else {
      try {
        const data = JSON.parse(payload.text);
        if (data.type === 'platform' && data.platform === 'mobile') {
          onMobileChangeRatio && onMobileChangeRatio(payload.senderId, true);
        } else if (data.type === 'orientation') {
          onMobileChangeRatio && onMobileChangeRatio(payload.senderId, data.isLandscape);
        }
      } catch {
        console.log('Unknown command received:', payload.text);
      }
    }
  };

  useEffect(() => {
    zmClient.on('command-channel-message', onCommandMessage);
    return () => {
      zmClient.off('command-channel-message', onCommandMessage);
    };
  }, [zmClient, onCommandMessage]);

  useEffect(() => {
    if (isInstructor && !viewContext.galleryView && !viewContext.pinViewUser) {
      setGalleryViewStatus(GalleryViewStatus.Disabled);
    }
  }, [viewContext]);

  useParticipantsChange(zmClient, () => {
    if (zmClient) {
      setCommandReceivers([oneToAllUser, ...zmClient.getAllUser().filter((item) => item.userId !== currentUserId)]);
    }
  });

  useEffect(() => {
    if (zmClient) {
      setCommandReceivers([oneToAllUser, ...zmClient.getAllUser().filter((item) => item.userId !== currentUserId)]);
    }
  }, [currentUserId, zmClient]);

  return (
    <div className={classNames('video-footer', className)}>
      <PoseDetectionLoadingModal visible={poseDetectionLoading} />
      <div style={{ width: 220 }} />
      <div className="footer-center">
        {isAudioEnable && (
          <div style={styles.btnContainer}>
            <MicrophoneButton
              isStartedAudio={isStartedAudio}
              isMuted={isMuted}
              isSupportPhone={isSupportPhone}
              audio={audio}
              phoneCountryList={phoneCountryList}
              onPhoneCallClick={onPhoneCall}
              onPhoneCallCancel={onPhoneCallCancel}
              phoneCallStatus={getPhoneCallStatusDescription(phoneCallStatus)}
              onMicrophoneClick={onMicrophoneClick}
              onMicrophoneMenuClick={onMicrophoneMenuClick}
              microphoneList={micList}
              speakerList={speakerList}
              activeMicrophone={activeMicrophone}
              activeSpeaker={activeSpeaker}
              disabled={isComputerAudioDisabled}
              isMicrophoneForbidden={isMicrophoneForbidden || hasMicrophonePermission === false}
              isSecondaryAudioStarted={isSecondaryAudioStarted}
              className="microphone-button"
            />
            <Divider />
          </div>
        )}
        <div style={styles.btnContainer}>
          <CameraButton
            isStartedVideo={isStartedVideo}
            isCameraForbidden={hasCameraPermission === false}
            onCameraClick={onCameraClick}
            onSwitchCamera={onSwitchCamera}
            onMirrorVideo={onMirrorVideo}
            onSelectVideoProcessor={onProcessorClick}
            onVideoStatistic={() => {
              setSelectedStatisticTab('video');
              setStatisticVisible(true);
            }}
            onBlurBackground={onBlurBackground}
            onSelectVideoPlayback={onSelectVideoPlayback}
            activePlaybackUrl={activePlaybackUrl}
            activeProcessor={activeProcessor?.name}
            cameraList={cameraList}
            activeCamera={activeCamera}
            isMirrored={isMirrored}
            isBlur={isBlur}
          />
          <Divider />
        </div>
        {(hasMicrophonePermission === false || hasCameraPermission === false) && (
          <div className="permission-error-message">
            <p className="permission-error-text">
              Please enable access to your{' '}
              {hasMicrophonePermission === false && hasCameraPermission === false
                ? 'microphone and camera'
                : hasMicrophonePermission === false
                  ? 'microphone'
                  : 'camera'}
            </p>
            <a
              href="#"
              className="permission-error-link"
              onClick={(e) => {
                e.preventDefault();
                const modalType =
                  hasMicrophonePermission === false && hasCameraPermission === false
                    ? 'both'
                    : hasMicrophonePermission === false
                      ? 'microphone'
                      : 'camera';
                setPermissionModalType(modalType);
                setPermissionModalVisible(true);
              }}
            >
              {hasMicrophonePermission === false && hasCameraPermission === false
                ? 'Enable microphone and camera'
                : hasMicrophonePermission === false
                  ? 'Enable microphone'
                  : 'Enable camera'}
            </a>
          </div>
        )}

        {(zmClient.isHost() || zmClient.isManager()) && (
          <div style={styles.btnContainer}>
            <VideoBackgroundButton isBlur={isBlur} onBlurBackground={onBlurBackground} />
            <Divider />
          </div>
        )}

        {sharing && (
          <div style={styles.btnContainer}>
            <ScreenShareButton
              sharePrivilege={sharePrivilege}
              isHostOrManager={zmClient.isHost() || zmClient.isManager()}
              onScreenShareClick={onScreenShareClick}
              onSharePrivilegeClick={async (privilege) => {
                await mediaStream?.setSharePrivilege(privilege);
                setSharePrivileg(privilege);
              }}
            />
            {(isInstructor || recordingStatus === RecordingStatus.Recording) && <Divider />}
          </div>
        )}
        {(isInstructor || recordingStatus === RecordingStatus.Recording) &&
          !zmClient?.getCurrentUserInfo()?.subsessionId && (
            <div style={{ ...styles.btnContainer }}>
              <div style={{ ...styles.btnContainer, backgroundColor: '#D4183D', borderRadius: 50 }}>
                {recordingButtons.map((button: RecordButtonProps, index: number) => {
                  return (
                    <div style={{ marginRight: index === 1 ? 10 : 0, marginLeft: index === 1 ? 10 : 0 }}>
                      <RecordingButton
                        key={button.text}
                        onClick={() => {
                          onRecordingClick(button.text);
                        }}
                        {...button}
                        isStartingRecording={startingRecording}
                        isHost={isInstructor}
                      />
                    </div>
                  );
                })}
              </div>
              {isInstructor && <Divider />}
            </div>
          )}
        {isInstructor && poseDetectionButton && (
          <div style={styles.btnContainer}>
            <PoseDetectionButton
              {...poseDetectionButton}
              onClick={onPoseDetectionClick}
              disabled={!hasRequiredParticipants()}
            />
            <Divider />
          </div>
        )}
        {liveTranscriptionClient?.getLiveTranscriptionStatus().isLiveTranscriptionEnabled && (
          <div style={styles.btnContainer}>
            <LiveTranscriptionButton
              isStartedLiveTranscription={isStartedLiveTranscription}
              isDisableCaptions={isDisableCaptions}
              isHost={isInstructor}
              onDisableCaptions={onDisableCaptions}
              onLiveTranscriptionClick={onLiveTranscriptionClick}
            />
            <TranscriptionSubtitle text={caption.text} />
            <Divider />
          </div>
        )}
        {isSecondaryAudioStarted && (
          <div style={styles.btnContainer}>
            <Tooltip title="Secondary audio on">
              <SoundOutlined
                style={{ position: 'fixed', top: '45px', left: '10px', color: '#f60', fontSize: '24px' }}
              />
            </Tooltip>
            <Divider />
          </div>
        )}

        {isInstructor && galleryViewButton && (
          <div style={styles.btnContainer}>
            <GalleryViewButton
              {...galleryViewButton}
              onClick={onGalleryViewClick}
              disabled={!hasRequiredParticipants()}
            />
          </div>
        )}
      </div>

      <div className="footer-right">
        {fullScreenButton && <FullScreenButton {...fullScreenButton} onClick={onFullScreenClick} />}
        <LeaveButton onLeaveClick={handleLeaveClick} isHost={isInstructor} onEndClick={handleEndClick} />
      </div>

      <ConfirmOverlay
        visible={isLeaveConfirmVisible}
        title="Leave session"
        description="Are you sure you want to leave this session?"
        okText="Leave"
        cancelText="Cancel"
        onCancel={() => setIsLeaveConfirmVisible(false)}
        onOk={async () => {
          setIsLeaveConfirmVisible(false);
          await onLeaveClick();
        }}
      />

      <ConfirmOverlay
        visible={isEndConfirmVisible}
        title="End session for everyone"
        description="Are you sure you want to end this session for everyone?"
        okText="End session"
        cancelText="Cancel"
        onCancel={() => setIsEndConfirmVisible(false)}
        onOk={async () => {
          setIsEndConfirmVisible(false);
          await onEndClick();
        }}
      />

      <AudioVideoStatisticModal
        visible={statisticVisible}
        setVisible={setStatisticVisible}
        defaultTab={selecetedStatisticTab}
        isStartedAudio={isStartedAudio}
        isMuted={isMuted}
        isStartedVideo={isStartedVideo}
      />

      {recordingIsoStatus === RecordingStatus.Ask && (
        <IsoRecordingModal
          onClick={() => {
            recordingClient?.acceptIndividualRecording();
          }}
          onCancel={() => {
            recordingClient?.declineIndividualRecording();
          }}
        />
      )}
      {/* {!mediaStream?.isSupportVirtualBackground() && (
        <VideoMaskModel visible={videoMaskVisible} setVisible={setVideoMaskVisible} isMirrored={isMirrored} />
      )} */}
      <PermissionRequestModal
        visible={permissionModalVisible}
        type={permissionModalType}
        onRequestPermission={() => requestPermission(permissionModalType)}
        onCancel={() => setPermissionModalVisible(false)}
      />
      <PermissionInstructionModal
        visible={instructionModalVisible}
        deviceName={instructionDeviceName}
        instructions={instructionText}
        isChrome={
          (/Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)) || /Edg/.test(navigator.userAgent)
        }
        onClose={() => setInstructionModalVisible(false)}
      />
    </div>
  );
};
export default VideoFooter;

const styles: Record<string, CSSProperties> = {
  btnContainer: {
    flexDirection: 'row',
    display: 'flex',
    alignItems: 'center'
  }
};
